import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight } from 'lucide-react'

interface TimelinePaginationProps {
  currentPage: number
  totalPages: number
  totalEntries: number
  entriesPerPage: number
  onPageChange: (page: number) => void
}

export function TimelinePagination({
  currentPage,
  totalPages,
  totalEntries,
  entriesPerPage,
  onPageChange,
}: TimelinePaginationProps): React.ReactElement {
  const { t } = useTranslation('history')
  const start = (currentPage - 1) * entriesPerPage + 1
  const end = Math.min(currentPage * entriesPerPage, totalEntries)

  return (
    <div className="flex items-center justify-between border-t border-slate-200 pt-4 dark:border-slate-800">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t('showing', { start, end, total: totalEntries })}
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            onPageChange(Math.max(1, currentPage - 1))
          }}
          disabled={currentPage === 1}
          className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('prev')}
        </button>
        <span className="text-sm text-slate-600 dark:text-slate-400">
          {t('pageOf', { current: currentPage, total: totalPages })}
        </span>
        <button
          onClick={() => {
            onPageChange(Math.min(totalPages, currentPage + 1))
          }}
          disabled={currentPage === totalPages}
          className="flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          {t('next')}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
